import type { AdminView } from '../nav';

/**
 * admin-web's areas and the engagement code each reports on entry (ADR 0012 step 18). One table, for the
 * reason room-web and the portal have one: an unknown code refuses its whole batch and nothing retries.
 * `screens.test.js` checks the codes against the server's list and `ActivityCatalogTests` checks each is
 * an engagement type.
 *
 * <p>`console.admin_<area>`, the mirror of room-web's `console.room_<area>`: one staff console family,
 * with the app in the name because several areas exist in both consoles (templates, points systems) and
 * are different screens for different jobs. Each
 * is filed under the feature it is about; the platform's own tags and settings under Platform
 * configuration, the Overview under Consoles. An administrator is nobody's side, so these views are
 * collected but counted on neither side of the volume card.</p>
 */
export const ADMIN_SCREENS: Record<AdminView, string> = {
  overview: 'console.admin_overview_viewed',
  rooms: 'console.admin_rooms_viewed',
  tags: 'console.admin_tags_viewed',
  templates: 'console.admin_templates_viewed',
  points: 'console.admin_points_systems_viewed',
  settings: 'console.admin_settings_viewed',
};

/** Every admin-web area that reports nothing, and why (ADR 0012 step 29). */
export const UNTRACKED_VIEWS: Record<string, string> = {};

/** What `App.tsx` renders, each declared (ADR 0012 step 29, `screens.coverage.test.js`). */
export const APP_ELEMENTS: Record<
  string,
  { kind: 'view' } | { kind: 'screen'; screen: AdminView } | { kind: 'untracked'; reason: string }
> = {
  // The frame and its chrome are drawn around every area, which reports as itself.
  AppShell: { kind: 'view' },
  SessionActions: { kind: 'view' },
  About: { kind: 'view' },
  OverviewArea: { kind: 'view' },
  RoomsArea: { kind: 'view' },
  TagsArea: { kind: 'view' },
  TemplatesArea: { kind: 'view' },
  PointsSystemsArea: { kind: 'view' },
  SettingsArea: { kind: 'view' },
  AccessDenied: {
    kind: 'untracked',
    reason: 'This sign-in is not a platform administrator, so there is no use of admin-web to report.',
  },
  Spinner: { kind: 'untracked', reason: 'Loading: nothing has been read yet, so there is no screen to have viewed.' },
  ErrorRetry: {
    kind: 'untracked',
    reason: 'The API could not be reached, and a view report would go to the same API.',
  },
};
